import { useState, useEffect, useCallback } from "react";
import { API, useAuth } from "@/App";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  Trash2,
  Plus,
  Save,
  Loader2,
  DollarSign,
} from "lucide-react";

// Stable client-side keys for rate rows (so editing/removing rows doesn't
// shuffle bound inputs the way array-index keys would).
let rateKeyCounter = 0;
const nextRateKey = () => `rate-${++rateKeyCounter}`;

export const TravelRatesManager = () => {
  const { token } = useAuth();
  const [rates, setRates] = useState([]);
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchRates = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/travel-rates`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRates((response.data.rates || []).map((r) => ({ ...r, _key: nextRateKey() })));
      setNote(response.data.note || "");
    } catch (error) {
      toast.error("Failed to load travel rates");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchRates();
  }, [fetchRates]);

  const updateRate = (index, field, value) => {
    setRates((prev) => prev.map((r, i) => (i === index ? { ...r, [field]: value } : r)));
  };

  const addRate = () => {
    setRates((prev) => [...prev, { duration: "", regular: "", pse: "", _key: nextRateKey() }]);
  };

  const removeRate = (index) => {
    setRates((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    // Validate
    const cleaned = rates
      .map((r) => ({ duration: r.duration.trim(), regular: r.regular.trim(), pse: r.pse.trim() }))
      .filter((r) => r.duration || r.regular || r.pse);
    if (cleaned.some((r) => !r.duration || !r.regular || !r.pse)) {
      toast.error("Each rate row needs a duration, regular price, and PSE price");
      return;
    }
    setSaving(true);
    try {
      await axios.put(
        `${API}/admin/travel-rates`,
        { rates: cleaned, note },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Travel rates updated!");
      fetchRates();
    } catch (error) {
      toast.error("Failed to save travel rates");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-travel-rates">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-3xl text-charcoal">Travel Rates</h1>
          <p className="font-body text-stone-grey">Adjust the duration & donation rates shown on the Travel page</p>
        </div>
        <Button onClick={addRate} className="btn-primary" data-testid="add-rate-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Row
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="spinner" /></div>
      ) : (
        <div className="glass rounded-xl p-6">
          {/* Header Row */}
          <div className="grid grid-cols-12 gap-3 px-2 pb-3 mb-2 border-b border-sand">
            <span className="col-span-4 font-body text-xs tracking-widest uppercase text-stone-grey">Duration</span>
            <span className="col-span-3 font-body text-xs tracking-widest uppercase text-stone-grey">Regular</span>
            <span className="col-span-3 font-body text-xs tracking-widest uppercase text-stone-grey">PSE</span>
            <span className="col-span-2"></span>
          </div>

          {rates.length === 0 ? (
            <div className="text-center py-8">
              <DollarSign className="w-10 h-10 text-taupe mx-auto mb-3" />
              <p className="font-body text-stone-grey">No rates yet. Click "Add Row" to create one.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {rates.map((rate, index) => (
                <div key={rate._key} className="grid grid-cols-12 gap-3 items-center" data-testid={`rate-row-${index}`}>
                  <Input
                    className="form-input col-span-4"
                    placeholder="e.g. 3 Hours"
                    value={rate.duration}
                    onChange={(e) => updateRate(index, "duration", e.target.value)}
                    data-testid={`rate-duration-${index}`}
                  />
                  <Input
                    className="form-input col-span-3"
                    placeholder="$1,800"
                    value={rate.regular}
                    onChange={(e) => updateRate(index, "regular", e.target.value)}
                    data-testid={`rate-regular-${index}`}
                  />
                  <Input
                    className="form-input col-span-3"
                    placeholder="$4,500"
                    value={rate.pse}
                    onChange={(e) => updateRate(index, "pse", e.target.value)}
                    data-testid={`rate-pse-${index}`}
                  />
                  <div className="col-span-2 flex justify-end">
                    <Button variant="ghost" size="sm" onClick={() => removeRate(index)} data-testid={`remove-rate-${index}`}>
                      <Trash2 className="w-4 h-4 text-lipstick-red" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="mt-6">
            <Label className="font-body text-sm text-stone-grey mb-2 block">Footnote (shown below the table)</Label>
            <Textarea
              className="form-input"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Note: Please inquire further regarding longer arrangements."
              data-testid="rate-note-input"
            />
          </div>

          <div className="mt-6 flex gap-2">
            <Button onClick={handleSave} disabled={saving} className="btn-primary" data-testid="save-rates-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save Rates
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

